import { signal, useSignal } from "@preact/signals";
import { useEffect, useMemo } from "preact/hooks";
import { type Channel, connect } from "~/web/src/channel";
import { ErrorModal } from "~/web/src/error-modal";
import { Composer } from "~/web/src/project/composer";
import { DropZone } from "~/web/src/project/dropzone";
import { Feed, groupLines } from "~/web/src/project/feed";
import { pill } from "~/web/src/project/pill";
import { Sheet, type SheetMode } from "~/web/src/project/sheet";
import { isFilming } from "~/web/src/project/tool-names";
import { isRendered, TopPane } from "~/web/src/project/top-pane";
import { Topbar } from "~/web/src/topbar";

// The run view (mockup ux/06, stages 2–9): one channel per mounted page; top
// pane · feed · composer in one column, the sheet to the right. The pane
// override and the sheet mode are page-local, like the old tabs.
//
// The mission typed on /projects travels here in memory: Generate creates the
// row, records the text against the new id and navigates; the first snapshot
// sends it as the first chat message. A reload in that sub-second window loses
// the text — the project stands, the composer is empty.

export const pendingMission = signal<{ id: string; text: string } | null>(null);

export function Run({ id }: { id: string }) {
  const channel: Channel = useMemo(() => connect(id), [id]);
  useEffect(() => () => channel.close(), [channel]);
  const viewing = useSignal<string | null>(null);
  const sheet = useSignal<SheetMode | null>(null);

  const ready = channel.project.value !== null;
  useEffect(() => {
    const mission = pendingMission.value;
    if (!ready || mission?.id !== id) return;
    pendingMission.value = null;
    void channel.send({ kind: "chat", text: mission.text });
  }, [ready, channel, id]);

  // The director's open_tab push: show the file in the top pane.
  useEffect(() => channel.onOpenTab((name) => (viewing.value = name)), [channel, viewing]);

  // Expire the render estimate when it runs out (the server sends nothing at the end). A
  // queue position has no clock: the estimate replaces it when the render starts.
  const progress = channel.progress.value;
  useEffect(() => {
    if (!progress || !("seconds" in progress)) return;
    const t = setTimeout(() => {
      if (channel.progress.value?.key === progress.key) channel.progress.value = null;
    }, progress.seconds * 1000);
    return () => clearTimeout(t);
  }, [channel, progress]);

  const items = groupLines(channel.chat.value);
  const last = items[items.length - 1];
  const filming = last?.kind === "card" && isFilming(last.lines);
  const hasVideo = channel.files.value.some(isRendered);
  const project = channel.project.value;

  return (
    <div class="app">
      <Topbar
        name={project?.name ?? "…"}
        pill={pill(channel.agent.value, channel.connected.value, filming, hasVideo)}
      />
      {/* The stage is the row under the chrome; the sheet and the wide pane fill it from its
          own top, so the chrome can be any height (the verification banner adds to it). */}
      <div class="stage">
        <div class="run">
          <div class="top">
            <TopPane channel={channel} viewing={viewing} />
          </div>
          <Feed channel={channel} items={items} />
          <Composer channel={channel} sheet={sheet} />
        </div>
        <Sheet channel={channel} mode={sheet} viewing={viewing} />
      </div>
      <DropZone channel={channel} />
      <ErrorModal />
    </div>
  );
}
